import { useLayoutEffect, useRef, useState } from "react";

import { BTCheckbox } from "@btnet/core-components";

import { BdsIconCaretSmallRight, BdsText } from "@buildertrend/components";

import {
    type GridColumn,
    type IGridRowData,
} from "commonComponents/utilities/Grid/GridContainer.types";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import { GridCardCellRenderer } from "./GridCardCellRenderer";

interface IGridCardProps {
    row: IGridRowData;
    rowIndex: number;
    /**
     * All columns flagged `IsAlwaysIncluded` server-side. The first becomes the
     * card's headline; the rest render one per row below it. By default
     * secondary rows are value-only — the consumer opts specific columns into
     * a "Label: Value" treatment via `labeledColumnIds`.
     */
    cardColumns: GridColumn<unknown, unknown>[];
    /** Column IDs (matched against `column.id`) that should show a label. */
    labeledColumnIds?: readonly string[];
    isSelectable?: boolean;
    onSelectToggle?: (row: IGridRowData, rowIndex: number, checked: boolean) => void;
    "data-testid"?: string;
}

const INTERACTIVE_SELECTOR = "a, button, input, [role=button], [role=link]";

interface IGridCardFieldRowProps {
    column: GridColumn<unknown, unknown>;
    row: IGridRowData;
    rowIndex: number;
    showLabel: boolean;
}

/**
 * One non-headline row inside the card. For labeled rows, hides itself when
 * the rendered cell produced no visible text — so e.g. "Due Date" disappears
 * entirely when there's no due date. Detection runs after layout (before
 * paint) by inspecting the value cell's textContent, which catches empty
 * values regardless of their raw shape (null, "", or an object like
 * `{ date: null }` whose Cell renders nothing).
 */
const GridCardFieldRow: React.FC<IGridCardFieldRowProps> = ({
    column,
    row,
    rowIndex,
    showLabel,
}) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const [isHiddenEmpty, setIsHiddenEmpty] = useState(false);

    useLayoutEffect(() => {
        if (!showLabel) {
            return;
        }
        const valueText = (
            rowRef.current?.querySelector(".GridCard-fieldValue")?.textContent ?? ""
        ).trim();
        setIsHiddenEmpty(valueText === "");
    }, [showLabel, column, row, rowIndex]);

    if (isHiddenEmpty) {
        return null;
    }

    return (
        <div
            ref={rowRef}
            className="GridCard-fieldRow"
            data-testid={`GridCard-fieldRow-${column.id}`}
        >
            {showLabel && (
                <BdsText size="normal-sm" className="GridCard-fieldLabel">
                    {column.title || column.name}
                </BdsText>
            )}
            <BdsText size="normal-sm" className="GridCard-fieldValue">
                <GridCardCellRenderer column={column} row={row} rowIndex={rowIndex} />
            </BdsText>
        </div>
    );
};

/**
 * One row rendered as a card. The first cardable column becomes the headline.
 * Remaining columns render one per line as `Label: Value`.
 *
 * Tapping anywhere on the card (outside an inner interactive element) fires
 * the first `<a>` link found on the card — giving us whole-card navigation
 * without nesting anchors. The link can live in any column (often the
 * entity-link column like Warranty Title) regardless of which column ended
 * up as the visual headline.
 */
export const GridCard: React.FC<IGridCardProps> = ({
    row,
    rowIndex,
    cardColumns,
    labeledColumnIds,
    isSelectable,
    onSelectToggle,
    "data-testid": testid,
}) => {
    const [headlineColumn, ...secondaryColumns] = cardColumns;
    const labeledNames = new Set(labeledColumnIds ?? []);

    const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest(INTERACTIVE_SELECTOR) || target.closest(".GridCard-checkboxColumn")) {
            return;
        }
        const link = e.currentTarget.querySelector<HTMLAnchorElement>("a[href]");
        link?.click();
    };

    return (
        <div
            className="GridCard"
            data-testid={testid ?? `GridCard-${row.id}`}
            role="group"
            aria-labelledby={`GridCard-headline-${row.id}`}
            onClick={handleCardClick}
        >
            {isSelectable && (
                <div className="GridCard-checkboxColumn">
                    <BTCheckbox
                        id={`GridCard-select-${row.id}`}
                        data-testid={`GridCard-select-${row.id}`}
                        checked={!!row.isSelected}
                        onChange={(e) => onSelectToggle?.(row, rowIndex, e.target.checked)}
                    />
                </div>
            )}
            <div className="GridCard-mainSection">
                {headlineColumn && (
                    <div className="GridCard-headline" id={`GridCard-headline-${row.id}`}>
                        <BdsText size="distinct-sm">
                            <TextTruncate>
                                <GridCardCellRenderer
                                    column={headlineColumn}
                                    row={row}
                                    rowIndex={rowIndex}
                                />
                            </TextTruncate>
                        </BdsText>
                    </div>
                )}

                {secondaryColumns.map((column) => (
                    <GridCardFieldRow
                        key={column.id}
                        column={column}
                        row={row}
                        rowIndex={rowIndex}
                        showLabel={labeledNames.has(column.id)}
                    />
                ))}
            </div>
            <div className="GridCard-chevronSection" aria-hidden="true">
                <BdsIconCaretSmallRight size="md" />
            </div>
        </div>
    );
};
